<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>{{ user.name }}'s Signed documents</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th>Portal</th>
                                <th>Title</th>
                                <th>Version</th>
                                <th>Status</th>
                                <th>Signed At</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="doc in docs" v-if="docs && docs.length" :key="doc.id" class="text-left">
                                <td>{{ doc.portal?.name ?? '' }}</td>
                                <td>{{ doc.legal_document?.title ?? '' }}</td>
                                <td>{{ doc.legal_document?.version ?? '' }}</td>
                                <td>
                                    <v-chip :color="doc.acceptance_status ? 'success' : 'error'" size="small" label>
                                        {{ doc.acceptance_status ? 'Accepted' : 'Declined' }}
                                    </v-chip>
                                </td>
                                <td>{{ formatDate(doc.created_at) }}</td>
                                <td>
                                    <v-btn density="compact" variant="outlined" @click="showDetails(doc)">Details
                                    </v-btn>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="!docs" class="text-center" colspan="6">
                                    <v-progress-circular color="primary" indeterminate size="small"/>
                                </td>
                                <td v-else class="text-center" colspan="6">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button"
                       @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-if="selectedDoc" v-model="detailsDialogSwitch" max-width="600" scrollable>
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <div>
                    <span class="text-h6 font-weight-bold">{{ selectedDoc?.legal_document?.title }}</span>
                    <span class="text-body-2 ml-2 text-grey">v{{ selectedDoc?.legal_document?.version }}</span>
                </div>
                <v-chip :color="selectedDoc.acceptance_status ? 'success' : 'error'" size="small" label>
                    {{ selectedDoc.acceptance_status ? 'Accepted' : 'Declined' }}
                </v-chip>
            </v-card-title>

            <v-card-text>
                <!-- Signing Details -->
                <p class="text-subtitle-2 text-grey-darken-2 mb-2">Signing Details</p>
                <v-row dense class="mb-4">
                    <v-col cols="6">
                        <div class="text-caption text-grey">Signed At</div>
                        <div class="text-body-2">{{ formatDate(selectedDoc.created_at) }}</div>
                    </v-col>
                    <v-col cols="6">
                        <div class="text-caption text-grey">Time Spent</div>
                        <div class="text-body-2">{{ formatTimeSpent(selectedDoc.time_spent_seconds) }}</div>
                    </v-col>
                    <v-col cols="6">
                        <div class="text-caption text-grey">Scrolled to Bottom</div>
                        <div class="text-body-2">
                            <v-icon size="16" :color="selectedDoc.scrolled_to_bottom ? 'success' : 'error'" class="mr-1">
                                {{ selectedDoc.scrolled_to_bottom ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                            {{ selectedDoc.scrolled_to_bottom ? 'Yes' : 'No' }}
                        </div>
                    </v-col>
                    <v-col cols="6" v-if="selectedDoc.doc_hash_at_accept">
                        <div class="text-caption text-grey">Document Hash</div>
                        <div class="text-body-2 d-flex align-center">
                            <span class="text-truncate" style="max-width: 180px;">{{ selectedDoc.doc_hash_at_accept }}</span>
                            <v-btn icon="mdi-content-copy" size="x-small" variant="text"
                                   @click="copyToClipboard(selectedDoc.doc_hash_at_accept)"/>
                        </div>
                    </v-col>
                </v-row>

                <v-divider class="mb-4"/>

                <!-- Device & Network -->
                <p class="text-subtitle-2 text-grey-darken-2 mb-2">Device &amp; Network</p>
                <v-row dense class="mb-4">
                    <v-col cols="6" v-if="selectedDoc.ip_address">
                        <div class="text-caption text-grey">IP Address</div>
                        <div class="text-body-2">{{ selectedDoc.ip_address }}</div>
                    </v-col>
                    <v-col cols="12" v-if="selectedDoc.user_agent">
                        <div class="text-caption text-grey">User Agent</div>
                        <div class="text-body-2 text-truncate" style="max-width: 100%;">{{ formatUserAgent(selectedDoc.user_agent) }}</div>
                    </v-col>
                    <v-col cols="6" v-if="devicePlatform">
                        <div class="text-caption text-grey">Platform</div>
                        <div class="text-body-2">{{ devicePlatform }}</div>
                    </v-col>
                    <v-col cols="6" v-if="deviceAppVersion">
                        <div class="text-caption text-grey">App Version</div>
                        <div class="text-body-2">{{ deviceAppVersion }}</div>
                    </v-col>
                </v-row>

                <!-- Location (from geolocation) -->
                <template v-if="geoData">
                    <v-divider class="mb-4"/>
                    <p class="text-subtitle-2 text-grey-darken-2 mb-2">Location</p>
                    <v-row dense>
                        <v-col cols="6" v-if="geoData.countryName">
                            <div class="text-caption text-grey">Country</div>
                            <div class="text-body-2">{{ geoData.countryName }}</div>
                        </v-col>
                        <v-col cols="6" v-if="geoData.regionName">
                            <div class="text-caption text-grey">Region</div>
                            <div class="text-body-2">{{ geoData.regionName }}</div>
                        </v-col>
                        <v-col cols="6" v-if="geoData.cityName">
                            <div class="text-caption text-grey">City</div>
                            <div class="text-body-2">{{ geoData.cityName }}</div>
                        </v-col>
                        <v-col cols="6" v-if="geoData.latitude && geoData.longitude">
                            <div class="text-caption text-grey">Coordinates</div>
                            <div class="text-body-2">{{ geoData.latitude }}, {{ geoData.longitude }}</div>
                        </v-col>
                    </v-row>
                </template>
            </v-card-text>

            <v-card-actions class="justify-end">
                <v-btn size="small" @click="detailsDialogSwitch = false">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, onMounted, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    user: Object,
});

const emits = defineEmits(['update:modelValue']);
const dialogSwitch = ref(props.modelValue);
const detailsDialogSwitch = ref(false);
const selectedDoc = ref(null);
const docs = ref(null);
const errors = ref([]);

onMounted(() => {
    fetchDocs()
})

const showDetails = (doc) => {
    selectedDoc.value = doc;
    detailsDialogSwitch.value = true
}

const fetchDocs = () => {
    errors.value = [];
    send('get', `users/${props.user.id}/signed-legal-docs`)
        .then((response) => {
            docs.value = response.data.data;
        })
        .catch(function (errorResponse) {
            snackbar.showSnackbar(errorResponse ?? 'Something went wrong', 'error');
        })
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleString();
    } catch {
        return dateStr;
    }
};

const formatTimeSpent = (seconds) => {
    if (seconds == null) return '-';
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
};

const formatUserAgent = (ua) => {
    if (!ua) return '-';
    // Try to extract browser + OS from user agent string
    const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
    const osMatch = ua.match(/\(([^)]+)\)/);
    const parts = [];
    if (browserMatch) parts.push(browserMatch[0]);
    if (osMatch) parts.push(osMatch[1].split(';')[0].trim());
    return parts.length ? parts.join(' - ') : ua;
};

const deviceDetails = computed(() => {
    if (!selectedDoc.value?.device_details) return null;
    const dd = selectedDoc.value.device_details;
    try {
        return typeof dd === 'string' ? JSON.parse(dd) : dd;
    } catch { return null; }
});

const devicePlatform = computed(() => {
    const dd = deviceDetails.value;
    if (!dd) return null;
    const parts = [dd.platform, dd.version].filter(Boolean);
    return parts.length ? parts.join(' ') : null;
});

const deviceAppVersion = computed(() => deviceDetails.value?.app_version ?? null);

const geoData = computed(() => {
    if (!selectedDoc.value?.geolocation) return null;
    const geo = selectedDoc.value.geolocation;
    try {
        return typeof geo === 'string' ? JSON.parse(geo) : geo;
    } catch { return null; }
});

const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        snackbar.showSnackbar('Copied to clipboard', 'success');
    }).catch(() => {
        snackbar.showSnackbar('Failed to copy', 'error');
    });
};
</script>
